import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  Eye,
  Calendar,
  Users,
  PoundSterling,
  TrendingUp,
} from 'lucide-react';
import type { ProjectDetails, ProjectStatus, ProjectPriority } from '@/types/project';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface ActiveProjectsGridProps {
  projects: ProjectDetails[];
  onViewDetails: (project: ProjectDetails) => void;
}

type SortField = 'name' | 'code' | 'client' | 'status' | 'priority' | 'startDate' | 'endDate' | 'teamSize' | 'budget';
type SortDirection = 'asc' | 'desc';

export const ActiveProjectsGrid: React.FC<ActiveProjectsGridProps> = ({ 
  projects, 
  onViewDetails 
}) => {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'code':
          comparison = a.code.localeCompare(b.code);
          break;
        case 'client':
          comparison = a.clientName.localeCompare(b.clientName);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
          break;
        case 'startDate':
          comparison = a.timeline.startDate.getTime() - b.timeline.startDate.getTime();
          break;
        case 'endDate':
          comparison = a.timeline.endDate.getTime() - b.timeline.endDate.getTime();
          break;
        case 'teamSize':
          comparison = a.teamSize - b.teamSize;
          break;
        case 'budget':
          if (a.budget && b.budget) {
            const aPercent = (a.budget.usedHours / a.budget.totalHours) * 100;
            const bPercent = (b.budget.usedHours / b.budget.totalHours) * 100;
            comparison = aPercent - bPercent;
          }
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [projects, sortField, sortDirection]);

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20';
      case 'at-risk':
        return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20';
      case 'on-hold':
        return 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20';
      case 'planning':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
      case 'cancelled':
        return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
      case 'completed':
        return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
      default:
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
    }
  };

  const getPriorityColor = (priority: ProjectPriority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
      case 'medium':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
      case 'low':
        return 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20';
    }
  };

  const SortButton: React.FC<{ field: SortField; children: React.ReactNode }> = ({ field, children }) => (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 -ml-3 font-semibold"
      onClick={() => handleSort(field)}
    >
      {children}
      {sortField === field ? (
        sortDirection === 'asc' ? (
          <ArrowUp className="ml-2 h-4 w-4" />
        ) : (
          <ArrowDown className="ml-2 h-4 w-4" />
        )
      ) : (
        <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />
      )}
    </Button>
  );

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[120px]">
              <SortButton field="code">Code</SortButton>
            </TableHead>
            <TableHead className="min-w-[200px]">
              <SortButton field="name">Project Name</SortButton>
            </TableHead>
            <TableHead className="min-w-[180px]">
              <SortButton field="client">Client</SortButton>
            </TableHead>
            <TableHead className="w-[120px]">
              <SortButton field="status">Status</SortButton>
            </TableHead>
            <TableHead className="w-[100px]">
              <SortButton field="priority">Priority</SortButton>
            </TableHead>
            <TableHead className="w-[140px]">
              <SortButton field="startDate">Start Date</SortButton>
            </TableHead>
            <TableHead className="w-[140px]">
              <SortButton field="endDate">End Date</SortButton>
            </TableHead>
            <TableHead className="w-[100px] text-center">
              <SortButton field="teamSize">Team</SortButton>
            </TableHead>
            <TableHead className="w-[180px]">
              <SortButton field="budget">Budget Used</SortButton>
            </TableHead>
            <TableHead className="w-[100px]">Billing</TableHead>
            <TableHead className="w-[100px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedProjects.length === 0 ? (
            <TableRow>
              <TableCell colSpan={11} className="h-24 text-center text-muted-foreground">
                No projects found
              </TableCell>
            </TableRow>
          ) : (
            sortedProjects.map((project) => (
              <TableRow key={project.id} className="hover:bg-muted/50">
                <TableCell>
                  <Badge variant="outline" className="font-mono text-xs">
                    {project.code}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="font-medium">{project.name}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">
                    {project.description}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium">{project.clientName}</div>
                  <Badge variant="secondary" className="text-xs mt-1">
                    {project.clientSector.replace('-', ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={getStatusColor(project.status)}>
                    {project.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={getPriorityColor(project.priority)}>
                    {project.priority}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-sm">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                    {format(project.timeline.startDate, 'MMM d, yyyy')}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-sm">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                    {format(project.timeline.endDate, 'MMM d, yyyy')}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Users className="h-3 w-3 text-muted-foreground" />
                    <span className="font-medium">{project.teamSize}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {project.budget ? (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          {project.budget.usedHours.toLocaleString()}h / {project.budget.totalHours.toLocaleString()}h
                        </span>
                        <span className="font-medium">
                          {project.budget.totalHours > 0 
                            ? Math.round((project.budget.usedHours / project.budget.totalHours) * 100) 
                            : 0}%
                        </span>
                      </div>
                      <Progress 
                        value={project.budget.totalHours > 0 
                          ? (project.budget.usedHours / project.budget.totalHours) * 100 
                          : 0} 
                        className="h-1.5"
                      />
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">N/A</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <Badge variant="secondary" className="text-xs">
                      {project.billingType.replace('-', ' ')}
                    </Badge>
                    {project.billable && (
                      <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-700 dark:text-green-400 block w-fit">
                        Billable
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewDetails(project)}
                    className="h-8"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
